import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Listing.css';
import { FaArrowUp, FaArrowDown, FaTrash, FaEdit, FaEye, FaPlay, FaCopy, FaPlus, FaMinus } from 'react-icons/fa';
import moment from 'moment';
import Modal from 'react-modal';
import { toProperString } from '../../../utils/utility';

Modal.setAppElement('#root'); // Adjust this selector based on your app structure

const Listing = (props) => {
  // console.log("Listing", props);
  const { headers = [], data = [], meta = {}, actionsHandling, fetchingList, onSearch } = props;
  const { rowsPerPage = 20, showSearch = false, autoRefresh: enableAutoRefresh = false, refreshMS = 10000, defaultRefresh = false, headingLabel = '' } = meta;
  const enableCsvExport = !!(meta.exportAsCsv ?? meta.enableCsvExport ?? meta.showCsvExport);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortedData, setSortedData] = useState(data);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalDetails, setModalDetails] = useState({ title: '', description: '', action: null, rowData: null });
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
    setSortConfig({ key: '', direction: '' });
    setSortedData(data);
    setSearchTerm('');
  }, [data]);

  useEffect(() => {
    if (defaultRefresh)
      setAutoRefresh(true)
  }, [defaultRefresh]);

  useEffect(() => {
    if (!autoRefresh) return undefined;

    const id = setInterval(() => {
      onSearch && onSearch();
    }, refreshMS);

    return () => clearInterval(id);
  }, [autoRefresh, onSearch, refreshMS]);

  const filteredData = sortedData.filter((item) =>
    headers.some((header) => {
      if (header.type === 'hidden') return false;
      if (header.type === 'link') {
        if (header.isSearchable) {
          return String(item[header.id]?.value?.toLowerCase() || '').includes(searchTerm.toLowerCase());
        }
        return false;
      } else if (header.type !== 'action' && header.type !== 'checkbox') {
        return String(item[header.id] || '').toLowerCase().includes(searchTerm.toLowerCase());
      }
      return false;
    })
  );

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const iconMapping = {
    FaTrash: FaTrash,
    FaEdit: FaEdit,
    FaEye: FaEye,
    FaPlay: FaPlay,
    FaCopy: FaCopy,
    FaPlus: FaPlus,
    FaMinus: FaMinus,
  };

  const handleAction = (action, rowData) => {
    if (action.isConfirmationRequired) {
      setModalDetails({
        title: action.confirmationTitle || 'Confirm Action',
        description: action.confirmationDescription || 'Are you sure you want to proceed?',
        action,
        rowData,
      });
      setIsModalOpen(true);
    } else {
      actionsHandling(action, rowData);
    }
  };

  const handleConfirm = () => {
    if (modalDetails.action && modalDetails.rowData) {
      actionsHandling(modalDetails.action, modalDetails.rowData);
    }
    setIsModalOpen(false);
  };

  const renderActions = (actions, value, rowData) => {
    const filteredActions = actions.filter((action) => value.includes(action.id));
    return filteredActions.map((action, index) => {
      const IconComponent = iconMapping[action.icon];
      const buttonStyle = {
        backgroundColor: action.color || 'gray',
        color: 'white',
        border: 'none',
        cursor: 'pointer',
      };

      if (action.isIcon) {
        return (
          <button
            key={index}
            className="btn btn-sm mx-1"
            onClick={() => handleAction(action, rowData)}
            title={action.label}
            style={buttonStyle}
          >
            {IconComponent ? <IconComponent /> : action.label}
          </button>
        );
      }

      return (
        <button
          key={index}
          className="btn btn-sm mx-1"
          onClick={() => handleAction(action, rowData)}
          style={buttonStyle}
        >
          {action.label}
        </button>
      );
    });
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...data].sort((a, b) => {
      const aValue = a[key] || '';
      const bValue = b[key] || '';

      const filteredHeader = headers.find((header) => header.id === key);
      if (filteredHeader?.type === 'date') {
        const formats = [...(filteredHeader.valueFormats || []), filteredHeader?.valueFormat || filteredHeader?.format || 'YYYY-MM-DD'];
        const format = formats.find(fmt => moment(aValue, fmt, true).isValid()) || 'YYYY-MM-DD';

        const aDate = moment(aValue, format);
        const bDate = moment(bValue, format);

        // console.log("aValuw", aValue, format, aDate, aDate.isValid());
        // console.log("aValuw", bValue, format, bDate, bDate.isValid());
        if (!aDate.isValid() && !bDate.isValid()) return 0;
        if (!aDate.isValid()) return direction === 'asc' ? 1 : -1;
        if (!bDate.isValid()) return direction === 'asc' ? -1 : 1;

        return direction === 'asc' ? aDate - bDate : bDate - aDate;
      } else if (filteredHeader?.type === 'component') {
        const aCmpValue = aValue.value;
        const bCmpValue = bValue.value;
        if (aCmpValue < bCmpValue) return direction === 'asc' ? -1 : 1;
        if (aCmpValue > bCmpValue) return direction === 'asc' ? 1 : -1;
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setSortedData(sorted);
  };

  const toCsvCell = (value) => {
    const rawValue = value === null || value === undefined ? '' : String(value);
    return `"${rawValue.replace(/"/g, '""')}"`;
  };

  const getCsvValue = (item, header) => {
    const value = item[header.id];
    if (value === null || value === undefined) return '';
    if (header.type === 'link') return value?._value || value?.value || value?.link || '';
    if (header.type === 'enum') return toProperString(value);
    if (header.type === 'date') {
      const customFormat = header.customFormat || {};
      const customFormatKeys = Object.keys(customFormat);
      let format = header.format || 'YYYY-MM-DD';
      customFormatKeys.forEach(fmt => {
        if (moment(value, fmt, true).isValid()) {
          format = customFormat[fmt];
        }
      });
      return moment(value).isValid() ? moment(value).format(format) : '';
    }
    if (header.type === 'component') return value?.value || '';
    if (typeof value === 'object') return value.value || JSON.stringify(value);
    return value;
  };

  const handleExportCsv = () => {
    const exportableHeaders = headers.filter((header) => !['action', 'checkbox', 'component', 'index'].includes(header.type));
    if (!exportableHeaders.length) return;

    const headerRow = exportableHeaders.map((header) => toCsvCell(header.label)).join(',');
    const bodyRows = filteredData.map((item) => {
      return exportableHeaders.map((header) => toCsvCell(getCsvValue(item, header))).join(',');
    });

    const csvContent = [headerRow, ...bodyRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const downloadLink = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const fileName = `${(headingLabel || 'listing').replace(/\s+/g, '_').toLowerCase()}.csv`;

    downloadLink.href = url;
    downloadLink.setAttribute('download', fileName);
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(url);
  };

  const normalizeComparableValue = (value, compareAs = 'string') => {
    if (compareAs === 'number') {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const parsedValue = Number(value.replace(/,/g, '').trim());
        return Number.isNaN(parsedValue) ? null : parsedValue;
      }
      return null;
    }

    if (compareAs === 'date') {
      const parsedDate = moment(value);
      return parsedDate.isValid() ? parsedDate.valueOf() : null;
    }

    return String(value ?? '').toLowerCase();
  };

  const doesRuleMatch = (cellValue, operator, targetValue, compareAs = 'string') => {
    if (cellValue === null || cellValue === undefined) return false;

    const normalizedTarget =
      operator === 'between' && Array.isArray(targetValue)
        ? targetValue.map((value) => normalizeComparableValue(value, compareAs))
        : normalizeComparableValue(targetValue, compareAs);

    switch (operator) {
      case '<':
        return cellValue < normalizedTarget;
      case '<=':
        return cellValue <= normalizedTarget;
      case '>':
        return cellValue > normalizedTarget;
      case '>=':
        return cellValue >= normalizedTarget;
      case '==':
        return cellValue === normalizedTarget;
      case '===':
        return cellValue === normalizedTarget;
      case '!=':
        return cellValue !== normalizedTarget;
      case '!==':
        return cellValue !== normalizedTarget;
      case 'contains':
        return String(cellValue).includes(String(normalizedTarget));
      case 'startsWith':
        return String(cellValue).startsWith(String(normalizedTarget));
      case 'endsWith':
        return String(cellValue).endsWith(String(normalizedTarget));
      case 'between':
        if (!Array.isArray(normalizedTarget) || normalizedTarget.length !== 2) return false;
        return cellValue >= normalizedTarget[0] && cellValue <= normalizedTarget[1];
      default:
        return false;
    }
  };

  const getCellTextStyle = (header, value) => {
    const { valueColorRules = [], valueColorDefault = '' } = header || {};
    if (!Array.isArray(valueColorRules) || valueColorRules.length === 0) return undefined;

    const compareAs = header.valueComparisonType || (header.type === 'number' ? 'number' : 'string');
    const normalizedValue = normalizeComparableValue(value, compareAs);

    const matchedRule = valueColorRules.find((rule) => {
      if (!rule || !rule.operator || rule.value === undefined) return false;
      return doesRuleMatch(normalizedValue, rule.operator, rule.value, compareAs);
    });

    if (matchedRule?.color) return { color: matchedRule.color };
    if (valueColorDefault) return { color: valueColorDefault };
    return undefined;
  };

  return (
    <div className="container-fluid listing-container mt-3 mt-md-4">
      {fetchingList ?
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div> :
        <>
          {headingLabel ? <h5
            className="search-box-heading text-primary mb-4"
            style={{ fontWeight: '600' }}
          >
            {headingLabel}
          </h5> : ''}
          {showSearch && (
            <div className="mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}

          {enableAutoRefresh && (
            <div className="d-flex justify-content-end mb-2">
              <button
                className={`btn btn-sm ${autoRefresh ? 'btn-success' : 'btn-outline-secondary'}`}
                onClick={() => setAutoRefresh((prev) => !prev)}
              >
                {autoRefresh ? 'Auto Refresh: On' : 'Auto Refresh: Off'}
              </button>
            </div>
          )}
          {enableCsvExport && (
            <div className="d-flex justify-content-end mb-2">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={handleExportCsv}
                disabled={!filteredData.length}
              >
                Export CSV
              </button>
            </div>
          )}
          <div className="table-responsive listing-table-wrapper">
            {filteredData.length === 0 ? (
              <div className="text-center p-3 text-muted">No data available</div>
            ) : (
              <table className="table table-striped table-hover mb-0 listing-table">
                <thead style={{ position: 'sticky', top: 0, zIndex: 0, backgroundColor: '#fff' }}>
                  <tr>
                    {headers.map((header, index) => {
                      if (header.type === 'hidden') {
                        return '';
                      }
                      return (
                        <th key={index} className="text-start listing-th" style={{ wordWrap: 'break-word' }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <span>{header.label}</span>
                            {header.isSortable && (
                              <button
                                className="btn btn-sm"
                                onClick={() => handleSort(header.id)}
                                style={{ background: 'none', border: 'none' }}
                              >
                                {sortConfig.key === header.id ? (
                                  sortConfig.direction === 'asc' ? (
                                    <FaArrowUp />
                                  ) : (
                                    <FaArrowDown />
                                  )
                                ) : (
                                  <FaArrowUp />
                                )}
                              </button>
                            )}
                          </div>
                        </th>
                      )
                    }
                    )}
                  </tr>
                </thead>
                <tbody>
                  {currentData.map((item, index) => (
                    <tr key={index}>
                      {headers.map((header, headerIndex) => {
                        if (header.type === 'hidden') {
                          return '';
                        }
                        const value = item[header.id] !== undefined ? item[header.id] : '';
                        switch (header.type) {
                          case 'index':
                            return <td key={headerIndex} className="text-start">{((currentPage - 1) * rowsPerPage) + index + 1}</td>;

                          case 'component':
                            return value.component;

                          case 'text':
                          case 'number':
                            return <td key={headerIndex} className="text-start" style={getCellTextStyle(header, value)}>{value}</td>;

                          case 'enum':
                            return <td key={headerIndex} className="text-start" style={getCellTextStyle(header, value)}>{toProperString(value)}</td>;

                          case 'link':
                            const { link = '', value: _value = 'Link' } = value;
                            return (
                              <td key={headerIndex} className="text-start">
                                {_value && link ? (
                                  <a
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="listing-link"
                                  >
                                    {_value}
                                  </a>
                                ) : (
                                  ''
                                )}
                              </td>
                            );

                          case 'date':
                            const customFormat = header.customFormat || {};
                            const customFormatKeys = Object.keys(customFormat);
                            let format = header.format || 'YYYY-MM-DD';
                            customFormatKeys.forEach(fmt => {
                              if (moment(value, fmt, true).isValid()) {
                                format = customFormat[fmt];
                              }
                            });
                            const formattedDate = moment(value).isValid() ? moment(value).format(format) : '';
                            return <td key={headerIndex} className="text-start" style={getCellTextStyle(header, value)}>{formattedDate}</td>;

                          case 'checkbox':
                            return (
                              <td key={headerIndex} className="text-start">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={value}
                                  onChange={() => {
                                    const updatedValue = !value;
                                    const updatedItem = { ...item, [header.id]: updatedValue };
                                    actionsHandling(header, updatedItem);
                                  }}
                                />
                              </td>
                            );

                          case 'action':
                            return (
                              <td key={headerIndex} className="text-start">
                                <div className="button-grid">
                                  {header.actions && renderActions(header.actions, value, item)}
                                </div>
                              </td>
                            );

                          default:
                            return <td key={headerIndex} className="text-start" style={getCellTextStyle(header, value)}>{value}</td>;
                        }
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {filteredData.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3 listing-pagination">
              <button
                className="btn btn-outline-primary btn-sm ms-2"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="text-muted ms-2 me-2">
                Page {currentPage} of {totalPages} ({(currentPage - 1) * rowsPerPage + 1}-{Math.min((currentPage) * rowsPerPage, filteredData.length)} of {filteredData.length})
              </span>
              <button
                className="btn btn-outline-primary btn-sm ms-2"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}


        </>}
      {/* Confirmation Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        contentLabel="Confirmation"
        style={{
          overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(400px, calc(100vw - 32px))',
            padding: '20px',
            borderRadius: '10px',
          },
        }}
      >
        <h4>{modalDetails.title}</h4>
        <p>{modalDetails.description}</p>
        <div className="d-flex justify-content-end mt-3">
          <button
            className="btn btn-secondary me-2"
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
          >
            Confirm
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Listing;
